import { Injectable, inject } from '@angular/core';
import { HttpService } from './http.service';
import {
  ActivateWithNewPasswordModel,
  ActivationInfo,
  ActivateUserModel,
  ChangePasswordModel,
  LoginModel,
  ResendRegistrationOtpModel,
  TwoFactorLoginModel,
  VerifyRegistrationOtpModel,
} from '../models/api-models';
import { EMPTY, Observable, catchError, map, of, throwError } from 'rxjs';
import {
  ILoginResponse,
  ITwoFactorQrCodeResponse,
} from '../types/response-types';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private httpService = inject(HttpService);
  private storageService = inject(StorageService);

  public route = inject(ActivatedRoute);
  public router = inject(Router);

  /**
   * Performs HTTP POST request for logging in
   * @param loginModel LoginModel
   * @returns Observable<ILoginResponse>
   */
  public login(loginModel: LoginModel): Observable<ILoginResponse> {
    const login$ = this.httpService
      .post<ILoginResponse>('api/users/login', loginModel)
      .pipe(
        catchError((error) => {
          let errorMessage;
          var apiMessage =
            error?.error?.ErrorMessage ?? error?.error?.Message ?? '';

          // PBI #2230 (TC-10): Trial-Ende ohne gueltiges Abo - statt einer Fehlermeldung
          // wird direkt zur Trial-Ende-Seite umgeleitet (analog zum bestehenden
          // ResponseCode-basierten Redirect in handleLoginResponse()).
          if (
            apiMessage === 'TRIAL_EXPIRED_BASIC' ||
            apiMessage === 'TRIAL_EXPIRED_PRO'
          ) {
            this.router.navigate(['/trial-expired'], {
              queryParams: {
                package: apiMessage === 'TRIAL_EXPIRED_PRO' ? 'pro' : 'basic',
                email: loginModel.Email,
              },
            });
            return EMPTY;
          }

          switch (apiMessage) {
            case 'USER_NOT_FOUND':
            case 'PASSWORD_INCORRECT':
              errorMessage =
                'Die Anmeldung ist fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';
              break;
            case 'USER_INACTIVE':
              errorMessage =
                'Ihr Account wurde deaktiviert, bitte kontaktieren Sie den Verantwortlichen Ihrer Firma.';
              break;
            case 'COMPANY_INACTIVE':
              errorMessage =
                'Ihre Firma wurde deaktiviert, bitte kontaktieren Sie unseren Support über den untenstehenden Hilfe-Link.';
              break;
            case 'USER_PENDING_VALIDATION':
              errorMessage =
                'Benutzer nicht aktiviert! Bitte führen Sie zuerst die Aktivierung des Benutzers mit dem erhaltenen E-Mail durch. Prüfen Sie allenfalls auch Ihren Spam-Ordner.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
        map((response) => {
          var { Token } = response;
          if (Token) {
            this.storageService.setToken(Token);
          }
          return response;
        }),
      );

    return login$;
  }

  /**
   * Exchanges the current (still valid) JWT for a freshly generated one with
   * up-to-date claims, without re-sending credentials. Used after onboarding
   * test-data generation, where the existing token's claims have gone stale.
   * @returns Observable<ILoginResponse>
   */
  public refreshToken(): Observable<ILoginResponse> {
    return this.httpService
      .post<ILoginResponse>('api/users/refresh-token', {})
      .pipe(
        map((response) => {
          const { Token } = response;
          if (Token) {
            this.storageService.setToken(Token);
          }
          return response;
        }),
      );
  }

  public logout() {
    this.storageService.clearAll();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Performs HTTP POST request for getting two factor qr code.
   * @param twoFactorLoginModel model
   * @returns Observable<ITwoFactorQrCodeResponse>
   */
  public twoFactorQrCode(
    twoFactorLoginModel: TwoFactorLoginModel,
  ): Observable<ITwoFactorQrCodeResponse> {
    const twoFactorQrCode$ = this.httpService.post<ITwoFactorQrCodeResponse>(
      'api/users/two-factor-qr-code',
      twoFactorLoginModel,
    );

    return twoFactorQrCode$;
  }

  /**
   * Performs HTTP POST request for logging in
   * @param twoFactorLoginModel AccountModel
   * @returns Observable<ILoginResponse>
   */
  public twoFactorLogin(
    twoFactorLoginModel: TwoFactorLoginModel,
  ): Observable<ILoginResponse> {
    const login$ = this.httpService
      .post<ILoginResponse>('api/users/two-factor', twoFactorLoginModel)
      .pipe(
        catchError((error) => {
          let errorMessage;
          switch (error?.error?.ErrorMessage) {
            case 'USER_NOT_FOUND':
            case 'PASSWORD_INCORRECT':
            case 'PIN_CODE_INVALID':
              errorMessage =
                'Die Anmeldung ist fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';
              break;
            case 'USER_INACTIVE':
              errorMessage =
                'Ihr Account wurde deaktiviert, bitte kontaktieren Sie den Verantwortlichen Ihrer Firma.';
              break;
            case 'COMPANY_INACTIVE':
              errorMessage =
                'Ihre Firma wurde deaktiviert, bitte kontaktieren Sie unseren Support über den untenstehenden Hilfe-Link.';
              break;
            case 'USER_PENDING_VALIDATION':
              errorMessage =
                'Benutzer nicht aktiviert! Bitte führen Sie zuerst die Aktivierung des Benutzers mit dem erhaltenen E-Mail durch. Prüfen Sie allenfalls auch Ihren Spam-Ordner.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
        map((response) => {
          var { Token } = response;
          this.storageService.setToken(Token);
          return response;
        }),
      );

    return login$;
  }

  public sendTwoFactorPinCodeViaEmail(
    loginModel: LoginModel,
  ): Observable<void> {
    const sendTwoFactorPinCodeViaEmail = this.httpService.post<void>(
      'api/users/send-two-factor-pin-code-via-email',
      loginModel,
    );
    return sendTwoFactorPinCodeViaEmail;
  }

  public forgotPassword(payload: { email: string }): Observable<void> {
    const forgotPassword$ = this.httpService.post<void>(
      'api/users/forgot-password',
      payload,
    );
    return forgotPassword$;
  }

  public changePassword(model: ChangePasswordModel): Observable<void> {
    const changePassword$ = this.httpService.post<void>(
      'api/users/change-password',
      model,
    );
    return changePassword$;
  }

  public activateUser(model: ActivateUserModel): Observable<void> {
    const activateUser$ = this.httpService
      .post<void>('api/users/activate', model)
      .pipe(
        catchError((error) => {
          let errorMessage;
          switch (error?.error?.ErrorMessage) {
            case 'USER_NOT_FOUND':
              errorMessage =
                'Die Anmeldung ist fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';
              break;
            case 'USER_NOT_PENDING_VALIDATION':
              errorMessage = 'Der Benutzer wurde schon aktiviert.';
              break;
            case 'ACTIVATION_FAILED':
              errorMessage =
                'Der Benutzer konnte nicht aktiviert werden. Möglicherweise ist das Aktivierungs E-Mail abgelaufen. Bitte kontaktieren Sie unseren Support über den untenstehenden Hilfe-Link.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
      );
    return activateUser$;
  }

  // PBI #2245: Informationen zum Aktivierungslink. Fehler -> null (Seite zeigt die bisherige Maske).
  public getActivationInfo(hash: string): Observable<ActivationInfo | null> {
    return this.httpService
      .get<ActivationInfo>(
        `api/users/activation-info?hash=${encodeURIComponent(hash)}`,
      )
      .pipe(catchError(() => of(null)));
  }

  // PBI #2245: eingeladener Mitarbeiter legt sein Passwort fest und wird direkt angemeldet.
  public activateWithNewPassword(
    model: ActivateWithNewPasswordModel,
  ): Observable<ILoginResponse> {
    return this.httpService
      .post<ILoginResponse>('api/users/activate-with-new-password', model)
      .pipe(
        catchError((error) => {
          let errorMessage;
          switch (error?.error?.ErrorMessage) {
            case 'PASSWORD_TOO_SHORT':
              errorMessage =
                'Das Passwort muss mindestens 6 Zeichen lang sein.';
              break;
            case 'ACTIVATION_LINK_INVALID':
              errorMessage =
                'Dieser Aktivierungslink ist ungültig oder wurde bereits verwendet. Bitte melden Sie sich an oder bitten Sie Ihre Firma, die Einladung erneut zu senden.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
        map((response) => {
          const { Token } = response;
          if (Token) {
            this.storageService.setToken(Token);
          }
          return response;
        }),
      );
  }

  // PBI #2228 (TC-08-2): Aktivierung ueber OTP-Code statt Aktivierungslink. Antwort ist
  // wie bei login()/twoFactorLogin() ein LoginResponse (JWT) - kein erneutes Passwort-Feld
  // noetig, der OTP-Code selbst ist der Nachweis der E-Mail-Inhaberschaft.
  public verifyRegistrationOtp(
    model: VerifyRegistrationOtpModel,
  ): Observable<ILoginResponse> {
    const verify$ = this.httpService
      .post<ILoginResponse>('api/users/verify-registration-otp', model)
      .pipe(
        catchError((error) => {
          let errorMessage;
          switch (error?.error?.ErrorMessage) {
            case 'USER_NOT_FOUND':
              errorMessage =
                'Die Anfrage ist fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';
              break;
            case 'USER_NOT_PENDING_VALIDATION':
              errorMessage = 'Der Benutzer wurde schon aktiviert.';
              break;
            case 'OTP_NOT_FOUND':
              errorMessage =
                'Es wurde noch kein Code angefordert. Bitte fordern Sie einen neuen Code an.';
              break;
            case 'OTP_EXPIRED':
              errorMessage =
                'Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.';
              break;
            case 'OTP_MAX_ATTEMPTS_REACHED':
              errorMessage =
                'Zu viele Fehlversuche. Bitte fordern Sie einen neuen Code an.';
              break;
            case 'OTP_INVALID':
              errorMessage = 'Der eingegebene Code ist ungültig.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
        map((response) => {
          var { Token } = response;
          if (Token) {
            this.storageService.setToken(Token);
          }
          return response;
        }),
      );

    return verify$;
  }

  public resendRegistrationOtp(
    model: ResendRegistrationOtpModel,
  ): Observable<void> {
    const resend$ = this.httpService
      .post<void>('api/users/resend-registration-otp', model)
      .pipe(
        catchError((error) => {
          let errorMessage;
          switch (error?.error?.ErrorMessage) {
            case 'OTP_RESEND_COOLDOWN':
              errorMessage =
                'Bitte warten Sie kurz, bevor Sie einen neuen Code anfordern.';
              break;
            case 'OTP_RESEND_LIMIT_REACHED':
              errorMessage =
                'Sie haben die maximale Anzahl an Codes angefordert. Bitte kontaktieren Sie unseren Support über den untenstehenden Hilfe-Link.';
              break;
            case 'USER_NOT_PENDING_VALIDATION':
              errorMessage = 'Der Benutzer wurde schon aktiviert.';
              break;
            default:
              errorMessage = 'Fehler aufgetreten.';
              break;
          }
          return throwError(() => new Error(errorMessage));
        }),
      );
    return resend$;
  }

  public handleLoginResponse(
    response: ILoginResponse,
    loginModel?: LoginModel,
  ) {
    switch (response.ResponseCode) {
      case 1:
        // NOTE: This is the default redirect logic - bear in mind that
        // is-authenticated.guard will handle the redirect to the required
        // settings page if necessary but this will usually happen after
        // the navigation to the app already happened
        const redirectTo = this.route.snapshot.queryParams['redirectTo'];
        if (redirectTo) this.router.navigate([redirectTo]);
        else this.router.navigate(['/app']);
        break;
      case 2:
        this.router.navigate(['/auth/two-factor-setup'], { state: loginModel });
        break;
      case 3:
        this.router.navigate(['/auth/two-factor-login'], { state: loginModel });
        break;
    }
  }
}
